import {getToken, isLoggedIn} from "./auth";
import i18next from "../i18next";
import environment from "../environment";

export interface RequestAdditionalParams {
    onSuccess?: Function,
    onError?: (error: string) => void,
    goToRedirectUrl?: Function,
    search?:     string,
    redirectToLogin?: Function,
    redirectToRegisterWithCompletionState?: Function,
    redirectToGroups?: Function,
    organizationId?: number,
    session_uuid?: string
}

// Routing rules. Anything not matched falls back to the legacy API on /api/v1.1/.
// Order matters: later matches overwrite earlier ones (last match wins).
// `method` is optional — when set, the rule only applies for the matching HTTP verb (uppercase).
//
// `host`:
//   - 'public-api' → REACT_APP_API_URL (use 'v1' or 'v1p1' as version)
//   - 'legacy'     → REACT_APP_LEGACY_API_URL (use 'v1' or 'v1.1' as version)
//
// Most legacy resources reach the default (legacy v1.1) and need no rule. A 'legacy' entry below
// is only required when a specific resource should hit /api/v1/ instead of /api/v1.1/ on the legacy
// host (typically because the backend exposes the route on both versions identically and we want
// to align the audit on a single version, easing eventual public-api migration).
type ApiHost = 'public-api' | 'legacy';
const ROUTES: Array<{pattern: RegExp | string, host: ApiHost, version: string, method?: string}> = [
    {pattern: /^authenticate(\/|$)/,                                            host: 'public-api', version: 'v1'},
    {pattern: /^users$/,                                                        host: 'public-api', version: 'v1'},
    {pattern: /^users\/[0-9]+$/,                                                host: 'public-api', version: 'v1',   method: 'GET'},
    {pattern: 'whoami',                                                         host: 'public-api', version: 'v1'},
    {pattern: 'countries',                                                      host: 'public-api', version: 'v1'},
    {pattern: /^cities(\?|$)/,                                                  host: 'public-api', version: 'v1'},
    {pattern: /^cart\/?[0-9]*/,                                                 host: 'public-api', version: 'v1p1'},
    {pattern: /^groups\/[0-9]*\/offers*/,                                       host: 'public-api', version: 'v1'},
    {pattern: /^batch-group-session-offers*/,                                   host: 'public-api', version: 'v1p1'},
    {pattern: /^groups\/[0-9]*$/,                                               host: 'public-api', version: 'v1'},
    {pattern: /^organizations\/[0-9]*\/programs\/groups*/,                      host: 'public-api', version: 'v1'},
    {pattern: /^organizations\/[0-9]*\/iframe\/locations*/,                     host: 'public-api', version: 'v1p1'},
    {pattern: /^organizations\/[0-9]*\/iframe\/locations\/unavailabilities*/,   host: 'public-api', version: 'v1p1'},
    {pattern: /^organizations\/[0-9]*\/activities*/,                            host: 'public-api', version: 'v1p1'},
    {pattern: /^activities\/[0-9]*\/sessions*/,                                 host: 'public-api', version: 'v1'},
    {pattern: /^activities\/[0-9]*\/groups*/,                                   host: 'public-api', version: 'v1'},
    {pattern: /^membership_templates\/[0-9]*\/offers*/,                         host: 'public-api', version: 'v1'},
    {pattern: /^users\/[0-9]*\/waiting_lists*/,                                 host: 'public-api', version: 'v1'},
    {pattern: /^users\/[0-9]+\/phone_numbers*/,                                 host: 'public-api', version: 'v1'},
    {pattern: /^users\/[0-9]+\/addresses*/,                                     host: 'public-api', version: 'v1'},
    {pattern: /^users\/[0-9]+\/family_members$/,                                host: 'public-api', version: 'v1',   method: 'GET'},
    {pattern: /^contacts*/,                                                     host: 'public-api', version: 'v1p1'},
    {pattern: /^organizations\/[0-9]+\/iframe\/css/,                            host: 'public-api', version: 'v1p1'},
    {pattern: /^custom-labels\/[^/]+$/,                                         host: 'public-api', version: 'v1'},

    // Legacy resources kept on /api/v1/ (instead of the default /api/v1.1/).
    {pattern: /^users\/[0-9]+$/,                                                host: 'legacy',     version: 'v1',   method: 'PATCH'},
    {pattern: /^users\/[0-9]+\/family_members$/,                                host: 'legacy',     version: 'v1',   method: 'POST'},
    {pattern: /^organizations\/[0-9]+\/memberships$/,                           host: 'legacy',     version: 'v1',   method: 'GET'},
];

const matches = (pattern: RegExp | string, resource: string) =>
    typeof pattern === 'string' ? resource === pattern : pattern.test(resource);

const hostUrl = (host: ApiHost): string => host === 'public-api'
    ? environment.REACT_APP_API_URL
    : environment.REACT_APP_LEGACY_API_URL;

const resolveApiUrl = (resource: string, method?: string): string => {
    let apiUrl = `${environment.REACT_APP_LEGACY_API_URL}/api/v1.1`;
    for (const {pattern, host, version, method: routeMethod} of ROUTES) {
        if (matches(pattern, resource) && (!routeMethod || routeMethod === method)) {
            apiUrl = `${hostUrl(host)}/api/${version}`;
        }
    }
    return apiUrl;
};

export const qidigoFetch = async (resource: string, init: RequestInit, requestAdditionalParams: RequestAdditionalParams = {}, apiUrl: string = resolveApiUrl(resource, init.method)): Promise<any> => {
    const defaults = {
        headers: {
            "Accept": "application/json",
            "Accept-Language": i18next.language,
            "Content-Type": "application/json",
        },
        mode: "cors",
        credentials: 'include'
    };
    const resultingInit = Object.assign({}, defaults, init);
    resultingInit.headers = Object.assign({}, defaults.headers, init.headers);

    const response = await fetch(`${apiUrl}/${resource}`, resultingInit);
    if (response.ok) {
        if (!!requestAdditionalParams.onSuccess) requestAdditionalParams.onSuccess();
        if (response.status === 204 || response.status === 202) {
            return {};
        }
        return response.json();
    }
    else {
        let responseJson;
        try {
            responseJson = await response.json();
        }
        catch {}
        if (!!requestAdditionalParams.onError) requestAdditionalParams.onError(responseJson ? responseJson.message : undefined);
        // eslint-disable-next-line no-throw-literal
        else throw {status: response.status, ...(responseJson || {})};
    }
};

export const qidigoAuthenticatedFetch = async (resource: string, init: RequestInit, requestAdditionalParams: RequestAdditionalParams = {}, apiUrl: string = resolveApiUrl(resource, init.method)): Promise<any> => {
    const loggedIn = await isLoggedIn();
    if (!loggedIn && !!requestAdditionalParams.redirectToLogin) requestAdditionalParams.redirectToLogin();
    const resultingInit = Object.assign({}, init);
    const token = getToken();
    if (token) {
        resultingInit.headers = Object.assign({}, getTokenRequestHeaders(token), init.headers);
    }

    try {
        return await qidigoFetch(resource, resultingInit, requestAdditionalParams, apiUrl);
    } catch(e) {
        // if call is forbidden, redirect to login, else re-throw
        if (e.status === '403' && !!requestAdditionalParams.redirectToLogin) {
            requestAdditionalParams.redirectToLogin();
        }
        else throw e;
    }
};

export const getTokenRequestHeaders = (token: string) => {
    return {
        "Authorization": `Bearer ${token}`,
        "Bearer-Authorization": `Bearer ${token}`
    }
};
